import { createSlice } from '@reduxjs/toolkit';
import forOwn from 'lodash/forOwn';

import isInstructorRequiredLesson from '@classCreation/utils/isInstructorRequiredLesson';
import parseAcademyTopicClassType from '@classCreation/utils/parseAcademyTopicClassType';

const initialState = {
  superBatches: [],
  library: null,
  academySchedule: null,
  startDate: null,
  academyTopics: {},
  errors: {},
  isConfirmationOpen: false,
  classCreationTrackerId: null,
  isTrackerOpen: false,
};

export const scheduleClassesForm = createSlice({
  name: 'scheduleClassesForm',
  initialState,
  reducers: {
    setLibrary: (state, action) => {
      state.library = action.payload;
    },
    setInitialAcademyTopics: (state, action) => {
      state.academyTopics = action.payload.reduce(
        (academyTopics, cls, index) => {
          const classType = parseAcademyTopicClassType(cls.academy_topic);
          const isInstructorRequired = isInstructorRequiredLesson(
            cls.academy_topic
          );

          return {
            ...academyTopics,
            [cls.academy_topic.id]: {
              id: cls.academy_topic.id,
              order: index,
              selected: false,
              primaryInstructor: null,
              secondaryInstructor: null,
              otherInstructors: [],
              dateOfTopic: null,
              classType,
              isInstructorRequired,
            },
          };
        },
        {}
      );
    },
    setAcademyTopicsSelected: (state, action) => {
      const { selected } = action.payload;
      forOwn(state.academyTopics, (academyTopic) => {
        // If same state then do nothing
        // This ensures that instructors are not reset for already selected
        // classes
        if (academyTopic.selected === selected) return;

        academyTopic.selected = action.payload.selected;
        academyTopic.primaryInstructor = null;
        academyTopic.secondaryInstructor = null;
        academyTopic.otherInstructors = [];
      });
    },
    setAcademyTopicsDate: (state, action) => {
      const { dates } = action.payload;
      forOwn(state.academyTopics, (academyTopic) => {
        academyTopic.dateOfTopic = dates[academyTopic.id] || null;
      });
    },
    setAcademyTopicSelected: (state, action) => {
      const academyTopic = state.academyTopics[action.payload.academyTopicId];
      academyTopic.selected = action.payload.selected;
      academyTopic.primaryInstructor = null;
      academyTopic.secondaryInstructor = null;
      academyTopic.otherInstructors = [];
    },
    setAcademyTopicDate: (state, action) => {
      const academyTopic = state.academyTopics[action.payload.academyTopicId];
      academyTopic.dateOfTopic = action.payload.dateOfTopic;
    },
    setInstructorForAll: (state, action) => {
      const { instructorType, instructor } = action.payload;
      forOwn(state.academyTopics, (academyTopic) => {
        if (!academyTopic.isInstructorRequired) return;

        if (!academyTopic.selected) return;

        academyTopic[instructorType] = instructor;
      });
    },
    setPrimaryInstructor: (state, action) => {
      const academyTopic = state.academyTopics[action.payload.academyTopicId];
      if (!academyTopic.isInstructorRequired) return;

      academyTopic.primaryInstructor = action.payload.instructor;
    },
    setSecondaryInstructor: (state, action) => {
      const academyTopic = state.academyTopics[action.payload.academyTopicId];
      if (!academyTopic.isInstructorRequired) return;

      academyTopic.secondaryInstructor = action.payload.instructor;
    },
    setOtherInstructors: (state, action) => {
      const academyTopic = state.academyTopics[action.payload.academyTopicId];
      if (!academyTopic.isInstructorRequired) return;

      academyTopic.otherInstructors = action.payload.instructors;
    },
    setScheduleClassesFormAcademySchedule(state, action) {
      state.academySchedule = action.payload;
      state.startDate = null;
    },
    setSuperBatches(state, action) {
      state.superBatches = action.payload;
    },
    setScheduleClassesFormStartDate(state, action) {
      state.startDate = action.payload;
    },
    setConfirmationOpen(state, action) {
      state.isConfirmationOpen = action.payload;
    },
    setScheduleClassesFormErrors(state, action) {
      state.errors = action.payload;
    },
    showTracker(state, action) {
      state.isTrackerOpen = true;
      state.isConfirmationOpen = false;
      state.classCreationTrackerId = action.payload.classCreationTrackerId;
    },
    resetScheduleClassesForm: (state) => {
      state.superBatches = [];
      state.library = null;
      state.academySchedule = null;
      state.startDate = null;
      state.academyTopics = {};
      state.errors = {};
      state.isConfirmationOpen = false;
      state.isTrackerOpen = false;
      state.classCreationTrackerId = null;
    },
  },
});

export const {
  setLibrary,
  setInitialAcademyTopics,
  setAcademyTopicsSelected,
  setAcademyTopicsDate,
  setAcademyTopicSelected,
  setAcademyTopicDate,
  setScheduleClassesFormAcademySchedule,
  setSuperBatches,
  setScheduleClassesFormErrors,
  setConfirmationOpen,
  setPrimaryInstructor,
  setSecondaryInstructor,
  setOtherInstructors,
  setInstructorForAll,
  setScheduleClassesFormStartDate,
  showTracker,
  resetScheduleClassesForm,
} = scheduleClassesForm.actions;

export default scheduleClassesForm.reducer;
